import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockedNumbers } from './hooks/useBlockedNumbers';
import { BlockedNumbersTable, AddBlockedNumberModal } from './components';
import { PageLoader, Button, FullWidthContainer } from '../../components';
import { useToast } from '../../contexts/useToast';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { AddBlockedNumberRequest } from './types';

const BlockedNumbers: React.FC = () => {
  const { t } = useTranslation();
  const { blockedNumbers, loading, error, addBlockedNumber, removeBlockedNumber } =
    useBlockedNumbers();
  const { showToast } = useToast();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });

  const handleAdd = async (data: AddBlockedNumberRequest) => {
    const result = await addBlockedNumber(data);
    if (result.success) {
      showToast({
        type: 'success',
        title: t('blockedNumbers.numberBlocked'),
        message: t('blockedNumbers.numberBlockedMessage', { number: data.number }),
      });
    }
    return result;
  };

  const handleDelete = async (id: string) => {
    const entry = blockedNumbers.find(n => n.id === id);
    setDeletingId(id);
    try {
      const result = await removeBlockedNumber(id);
      if (result.success) {
        showToast({
          type: 'success',
          title: t('blockedNumbers.numberUnblocked'),
          message: t('blockedNumbers.numberUnblockedMessage', { number: entry?.number || id }),
        });
      } else {
        showToast({
          type: 'error',
          title: t('blockedNumbers.unblockError'),
          message: result.error || t('blockedNumbers.unblockErrorMessage'),
        });
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('blockedNumbers.title')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('blockedNumbers.description')}
            </p>
          </div>
          <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
            <span className="material-icons text-base mr-2">block</span>
            {t('blockedNumbers.addBlockedNumber')}
          </Button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <BlockedNumbersTable
          blockedNumbers={blockedNumbers}
          onDelete={handleDelete}
          deletingId={deletingId}
        />

        <AddBlockedNumberModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAdd}
        />
      </div>
    </FullWidthContainer>
  );
};

export { BlockedNumbers };
